import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { AuthWordmark } from "./AuthWordmark";
import { PasswordInput } from "./PasswordInput";
import { SsoButtons } from "./SsoButtons";

// Dev convenience: prefill seeded credentials and show the hint only in dev.
const DEV = import.meta.env.DEV;

export function PublicLoginPage() {
  const { state, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // Honor ?next= for the post-login bounce-back, guarding against open redirects.
  const rawNext = params.get("next");
  const next =
    rawNext && rawNext.startsWith("/") && !rawNext.startsWith("//") ? rawNext : "/app";

  const [email, setEmail] = useState(DEV ? "jonathan@example.com" : "");
  const [password, setPassword] = useState(DEV ? "admin123" : "");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Already signed in → skip the form and go where they were headed.
  if (state.kind === "authenticated") {
    return <Navigate to={next} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate(next, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
      setLoading(false);
    }
  };

  return (
    <div className="auth-screen">
      <div className="auth-card-wrap">
        <AuthWordmark />

        <form onSubmit={handleSubmit}>
          <div className="card">
            <div className="card-body auth-stack">
              <div className="auth-title">Sign in</div>

              <SsoButtons next={next} />

              {error && <div className="form-error" role="alert">{error}</div>}

              <label className="field">
                <span className="field-label">Email</span>
                <input
                  type="email"
                  className="field-input"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </label>

              <label className="field">
                <span className="field-label field-label-row">
                  <span>Password</span>
                  <Link to="/forgot" className="field-link">Forgot password?</Link>
                </span>
                <PasswordInput
                  value={password}
                  onChange={setPassword}
                  autoComplete="current-password"
                  required
                />
              </label>

              <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                {loading ? "Signing in…" : "Sign in"}
              </button>
            </div>
          </div>
        </form>

        <p className="auth-foot">
          Have an invite? <Link to="/signup">Set up your account</Link>
        </p>
        <p className="auth-foot">
          No account yet? <Link to="/beta">Request beta access</Link>
        </p>
        {DEV && (
          <p className="auth-foot">
            Dev credentials pre-filled. Login with any seeded user.
          </p>
        )}
        <p className="auth-back">
          <Link to="/">← Back to home</Link>
        </p>
      </div>
    </div>
  );
}
